import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'

import { sortByKey, sortByDateKey } from '../../util/sorting'
import { attachmentStatuses, formatAttachmentStatusDropdownOptions } from '../../util/attachmentConsts'
import { Divider, FlexRow } from '../styles/Containers'
import { CondensedTable, HeaderRow, HeaderButton, EmptyText, UnfoldMoreIconRight } from '../styles/TableStyles'
import LetterRow from './LetterRow'

const UserColumn = styled.div`
  flex-basis: 12%;
  min-width: 75px;
`

const NameColumn = styled.div`
  flex-basis: ${props => (props.$large ? '50%' : '38%')};
`

const DateColumn = styled(FlexRow)`
  flex-basis: ${props => (props.$large ? '33%' : '25%')};
  gap: 5px;
  flex-wrap: wrap;
`

const StatusColumn = styled.div`
  flex-basis: 20%;
  text-align: right;
`

const ActionSpacer = styled.div`
  width: 29px;
  flex-shrink: 0;
`

const SORT_KEYS = {
  user: (a, b) => getLetterUserName(a).localeCompare(getLetterUserName(b)),
  name: sortByKey('name'),
  date: sortByDateKey('date'),
  status: (a, b) => getLetterStatusLabel(a).localeCompare(getLetterStatusLabel(b))
}

const DRAFT_HEADINGS = {
  user: 'Assigned',
  name: 'Draft Name',
  date: 'Last Modified',
  status: 'Status'
}

const SENT_HEADINGS = {
  user: 'Sent By',
  name: 'Letter Name',
  date: 'Sent'
}

function getLetterUserName(letter) {
  const user = letter.state === 'DRAFT' ? letter.assignedTo : letter.sentBy
  if (!user) return ''
  return `${user.firstName} ${user.lastName}`
}

function getLetterStatusLabel(letter) {
  return formatAttachmentStatusDropdownOptions().find(option => option.value === letter.status)?.label || ''
}

function LetterTable({ state, letters, onEditLetter = () => {}, handleSelectLetter = () => {} }) {
  const [sortBy, setSortBy] = useState('date')
  const [sortedLetters, setSortedLetters] = useState([])
  const [sortAscending, setSortAscending] = useState(true)

  const headings = state === 'DRAFT' ? DRAFT_HEADINGS : SENT_HEADINGS
  const isDraft = state === 'DRAFT'

  function sort(key) {
    if (key === sortBy) {
      setSortAscending(prev => !prev)
    } else {
      setSortBy(key)
      setSortAscending(true)
    }
  }

  useEffect(() => {
    if (letters && letters.length > 0) {
      const comparator = SORT_KEYS[sortBy]
      let next = [...letters].sort(comparator)
      if (!sortAscending) next = next.reverse()
      setSortedLetters(next)
    } else {
      setSortedLetters([])
    }
  }, [letters, sortBy, sortAscending])

  useEffect(() => {
    const availableSortKeys = Object.keys(state === 'DRAFT' ? DRAFT_HEADINGS : SENT_HEADINGS)
    if (!availableSortKeys.includes(sortBy)) {
      setSortBy('date')
      setSortAscending(true)
    }
  }, [state, sortBy])

  return (
    <CondensedTable>
      <HeaderRow>
        <UserColumn>
          <HeaderButton variant="text" onClick={() => sort('user')} active={(sortBy === 'user').toString()}>
            {headings.user} <UnfoldMoreIcon />
          </HeaderButton>
        </UserColumn>
        <NameColumn $large={!isDraft}>
          <HeaderButton variant="text" onClick={() => sort('name')} active={(sortBy === 'name').toString()}>
            {headings.name} <UnfoldMoreIcon />
          </HeaderButton>
        </NameColumn>
        <DateColumn $large={!isDraft}>
          <HeaderButton variant="text" onClick={() => sort('date')} active={(sortBy === 'date').toString()}>
            {headings.date} <UnfoldMoreIcon />
          </HeaderButton>
        </DateColumn>
        {isDraft && (
          <StatusColumn>
            <HeaderButton variant="text" onClick={() => sort('status')} active={(sortBy === 'status').toString()}>
              {headings.status} <UnfoldMoreIconRight />
            </HeaderButton>
          </StatusColumn>
        )}
        <ActionSpacer />
      </HeaderRow>
      <Divider />
      {sortedLetters && sortedLetters.length > 0 ? (
        sortedLetters.map(letter => {
          return (
            <React.Fragment key={letter.id}>
              <LetterRow letter={letter} handleSelectLetter={handleSelectLetter} onEditLetter={onEditLetter} />
              <Divider />
            </React.Fragment>
          )
        })
      ) : (
        <EmptyText>No letters available</EmptyText>
      )}
    </CondensedTable>
  )
}

LetterTable.propTypes = {
  state: PropTypes.oneOf(['DRAFT', 'SENT']).isRequired,
  letters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      state: PropTypes.oneOf(['DRAFT', 'SENT']).isRequired,
      name: PropTypes.string.isRequired,
      date: PropTypes.instanceOf(Date).isRequired,
      status: PropTypes.oneOf(attachmentStatuses),
      assignedTo: PropTypes.object,
      sentBy: PropTypes.object
    })
  ).isRequired,
  onEditLetter: PropTypes.func,
  handleSelectLetter: PropTypes.func
}

export default LetterTable
